import { AgendaMotionRow, MemberIndexEntry } from '../../types/types';
import { Tone } from '../../theme';
import { getAgendaMotions, getAllMembersOf, getCommitteeList } from '../../data/cache';

/* Motions for the agenda (הצעות לסדר היום): shared helpers for the MK page section, the
   committee tab and the list page (/queries?tab=motions). Rows come from agendaMotionList
   (getAgendaMotions); see "agendaMotionList" in CLAUDE.md. */

// The four types, in the order the filters list them (5d-A)
export const MOTION_TYPES = ['רגילה', 'דחופה', 'דיון מהיר', 'בתקופת פגרה'];

// Phone chips use a short label for the long type
export const MOTION_TYPE_SHORT: Record<string, string> = { 'בתקופת פגרה': 'בפגרה' };

export const motionTypeTone = (type: string | null): Tone =>
  type === 'דחופה' ? 'warning' : type === 'דיון מהיר' ? 'info' : 'neutral';

// Settled outcomes are green, a stopped motion red, anything still pending neutral
const DONE_STATUSES = ['הסתפקות במסקנות הוועדה', 'הונחו מסקנות הוועדה', 'התקיים דיון במליאה', 'הונחו כל הודעות השרים'];
export const motionStatusTone = (status: string | null): Tone =>
  status === 'נעצרה' ? 'danger' : status && DONE_STATUSES.indexOf(status) >= 0 ? 'success' : 'neutral';

/* The list's status filter groups the Knesset's statuses (KNS_Status names, 11 in K25) into
   four (owner's mapping, 2026-10-17). Rows keep their exact status in the badge. */
export type StatusGroup = 'committee' | 'plenum' | 'discussed' | 'stopped';
export const STATUS_GROUPS: { key: StatusGroup; label: string }[] = [
  { key: 'committee', label: 'לדיון בוועדה' },
  { key: 'plenum', label: 'לדיון במליאה' },
  { key: 'discussed', label: 'נדונה' },
  { key: 'stopped', label: 'נעצרה' },
];
const STATUS_GROUP_OF: Record<string, StatusGroup> = {
  'לדיון בוועדה': 'committee',
  'קביעת ועדה מטפלת': 'committee',
  'הנמקת ההצעה במליאה': 'plenum',
  'לדיון במליאה': 'plenum',
  'במעקב - הנחת הודעת שר': 'discussed',
  'הונחו כל הודעות השרים': 'discussed',
  'הסתפקות במסקנות הוועדה': 'discussed',
  'הונחו מסקנות הוועדה': 'discussed',
  'התקיים דיון במליאה': 'discussed',
  'הסתפקות חה"כ בעמדת הממשלה': 'discussed',
  'נעצרה': 'stopped',
};
// A status's group; one not in the map goes by its wording (stopped, plenum, committee),
// else counts as discussed
export const statusGroup = (status: string | null): StatusGroup => {
  const s = (status || '').trim();
  if (Object.prototype.hasOwnProperty.call(STATUS_GROUP_OF, s)) return STATUS_GROUP_OF[s];
  if (s.indexOf('נעצר') >= 0) return 'stopped';
  if (s.indexOf('לדיון במליאה') === 0 || s.indexOf('הנמקת') === 0) return 'plenum';
  if (s.indexOf('לדיון בוועדה') === 0 || s.indexOf('קביעת ועדה') === 0) return 'committee';
  return 'discussed';
};

/* The subject without the Knesset's prefix ("הצעה לדיון מהיר בנושא:"), since the type has
   its own badge, without quotes around the whole of it, and without a closing
   "- של חברי הכנסת …" (the initiators are listed). Other subjects stay as they are. */
const SUBJECT_PREFIX = /^\s*(?:הצעה\s+)?(?:(?:רגילה|דחופה)\s+)?(?:לסדר(?:\s+היום)?|לסד|לדיון\s+מהיר|דיון\s+מהיר)(?:\s+בנושא\s*:?|\s*:)\s*/;
// The MKs' names after a dash, or right after the closing quote (which stays)
const SUBJECT_BY = /(\s*[-–—]\s*|["״”]\s*)של\s+(?:חבר|חברת|חברי)\s+הכנסת:?\s+[^"״”]+$/;
const QUOTE_CHARS = /["״“”]/g;
const isQuote = (c: string) => /["״“”]/.test(c);
// A quote between two letters is gershayim (צה"ל), not a quotation mark
const GERSHAYIM = /[א-ת]["״”][א-ת]/g;
const quotationMarks = (t: string): number => (t.match(QUOTE_CHARS) || []).length - (t.match(GERSHAYIM) || []).length;
export const motionSubject = (subject: string): string => {
  let s = (subject || '').replace(SUBJECT_PREFIX, '')
    .replace(SUBJECT_BY, (all: string, lead: string) => (/["״”]/.test(lead) ? lead.trim() : ''))
    .trim();
  // An unpaired quotation mark at either end (the Knesset's text drops some) goes
  if (quotationMarks(s) % 2 === 1) {
    if (isQuote(s.charAt(0))) s = s.slice(1).trim();
    else if (isQuote(s.charAt(s.length - 1))) s = s.slice(0, -1).trim();
  }
  // Quotes around the whole subject: only when the quotation marks inside are paired
  const inner = s.slice(1, -1);
  if (s.length > 2 && isQuote(s.charAt(0)) && isQuote(s.charAt(s.length - 1)) && quotationMarks(inner) % 2 === 0) {
    s = inner.trim();
  }
  return s || subject;
};

// "2026-07-06T13:18:49.29+03:00" → "6.7.2026" (the date is Israel-local, so it's read as written)
export const motionDate = (date: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '');
  return m ? `${Number(m[3])}.${Number(m[2])}.${m[1]}` : '';
};

// "דיון בהוועדה לביטחון לאומי" (as the Knesset site writes it) → "דיון בוועדה לביטחון לאומי"
export const discussionLabel = (label: string): string => (label || 'דיון').replace(/בהוועדה/g, 'בוועדה');

// The motion's own page on the Knesset site (its Agenda app routes details/{KNS_Agenda Id}):
// subject, type, status, the MKs who moved it and their proposal documents
export const motionUrl = (id: string): string | null => {
  const n = /^am_(\d+)$/.exec(id);
  return n ? `https://main.knesset.gov.il/apps/agenda/details/${n[1]}` : null;
};

// Everyone who moved it: member docs first, then the names no member doc matched
export const initiatorCount = (m: AgendaMotionRow): number => m.mkIds.length + (m.otherNames ? m.otherNames.length : 0);

// Whether a motion matches a search: its subject (as shown) or one of its MKs' names
export const motionMatches = (m: AgendaMotionRow, q: string, members: Record<string, MemberIndexEntry>): boolean => {
  if (!q) return true;
  if (motionSubject(m.subject).indexOf(q) >= 0) return true;
  for (let i = 0; i < m.mkIds.length; i++) {
    const mk = members[m.mkIds[i]];
    if (mk && mk.name.indexOf(q) >= 0) return true;
  }
  return (m.otherNames || []).some(n => n.indexOf(q) >= 0);
};

export interface AgendaMotionData {
  motions: AgendaMotionRow[];
  // The MKs of Knesset k (names and photos for the initiators)
  members: Record<string, MemberIndexEntry>;
  // Committee doc ID → name
  committees: Record<string, string>;
}

// Knesset k's motions, with the names they refer to. Names are optional: a failed read
// leaves them out (rows then show the other names only).
export const loadAgendaMotionData = async (k: number | undefined): Promise<AgendaMotionData> => {
  const [motions, members, committees] = await Promise.all([
    getAgendaMotions(k),
    getAllMembersOf(k).catch(() => [] as MemberIndexEntry[]),
    getCommitteeList().catch(() => []),
  ]);
  const byId: Record<string, MemberIndexEntry> = {};
  members.forEach(m => { byId[m.id] = m; });
  const names: Record<string, string> = {};
  committees.forEach(c => { names[c.id] = c.name; });
  return { motions, members: byId, committees: names };
};
